/**
 * The phone's docked keypad (PROD-1511): the app's own count pad, the same
 * keys `CountInput` shows on a tablet (`1-9`, `✓`, `0`, `⌫`; the decimal layout
 * for a temperature), docked across the bottom of the screen instead of a
 * popover over a box, so the value being typed stays visible above it.
 *
 * The header names what is being typed (`Fruit Cup K-12 · Served`) and what it
 * is checked against (`Received 39`); up and down move to the previous or next
 * value. A temperature can show its quick presets (`Hot · 165.0°F`) as chips
 * over the keys, the same presets the dropdown offers. `✓` closes the pad.
 *
 * Presentation only: every key goes to `onKey`, the caller owns the value.
 */

import { Button } from '@ordo/ui/button'
import { cn } from '@ordo/ui/cn'
import { ChevronDown, ChevronUp } from 'lucide-react'

import { NumericKeypad } from '@/components/cafeteria/count-input'
import type { NumericKeypadKey } from '@/lib/cafeteria/numeric-keypad'

export interface DockedKeypadPreset {
  key: string
  label: string
}

export interface DockedKeypadProps {
  /** `Fruit Cup K-12 · Served`. */
  title: string
  /** What the value is checked against, e.g. `Received 39`. */
  subtitle?: string | null
  /** The temperature layout: `.` bottom-left, Done on a row of its own. */
  decimal?: boolean
  presets?: readonly DockedKeypadPreset[]
  /** The preset the value currently equals, drawn pressed. */
  activePresetKey?: string | null
  onPreset?: (key: string) => void
  /** Every key but Done. */
  onKey: (key: NumericKeypadKey) => void
  /** Null at the first value. */
  onPrev?: (() => void) | null
  /** Null at the last value. */
  onNext?: (() => void) | null
  /** The next value's name, for the down button's accessible name. */
  nextName?: string | null
  onDone: () => void
}

export function DockedKeypad({
  title,
  subtitle = null,
  decimal = false,
  presets = [],
  activePresetKey = null,
  onPreset,
  onKey,
  onPrev = null,
  onNext = null,
  nextName = null,
  onDone,
}: DockedKeypadProps) {
  return (
    <section
      aria-label={`Keypad for ${title}`}
      data-docked-keypad
      className="fixed inset-x-0 bottom-0 z-50 border-t bg-background px-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-3 shadow-[0_-6px_16px_rgba(0,0,0,0.08)]">
      <div className="mx-auto flex max-w-md flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-[15px] font-semibold leading-[21px]">{title}</p>
            {subtitle ? <p className="text-[13px] leading-[18px] text-muted-foreground">{subtitle}</p> : null}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 shrink-0 rounded-[10px]"
            aria-label="Previous value"
            disabled={onPrev === null}
            onClick={() => onPrev?.()}>
            <ChevronUp className="size-5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-11 shrink-0 rounded-[10px]"
            aria-label={nextName ? `Next value, ${nextName}` : 'Next value'}
            disabled={onNext === null}
            onClick={() => onNext?.()}>
            <ChevronDown className="size-5" aria-hidden="true" />
          </Button>
        </div>
        {presets.length > 0 ? (
          <div className="grid grid-cols-2 gap-2">
            {presets.map((preset) => (
              <button
                key={preset.key}
                type="button"
                aria-pressed={activePresetKey === preset.key}
                className={cn(
                  'h-11 rounded-full border text-sm font-medium transition-colors',
                  activePresetKey === preset.key ? 'border-primary bg-primary/5' : 'border-border bg-background',
                )}
                onClick={() => onPreset?.(preset.key)}>
                {preset.label}
              </button>
            ))}
          </div>
        ) : null}
        <NumericKeypad stretch decimal={decimal} onKey={(key) => (key.kind === 'confirm' ? onDone() : onKey(key))} />
      </div>
    </section>
  )
}
